import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
import api from "../services/api";
import SearchInput from "../components/SearchInput";
import "./Wiki.css";

interface WikiPage {
  id: number | string;
  title: string;
  content?: string;
  folder_id?: number | string | null;
}

interface Folder {
  id: number | string;
  name: string;
  parent_id?: number | string | null;
}

interface TreeNode {
  id: number | string | null;
  name: string;
  parent_id?: number | string | null;
  folders: TreeNode[];
  pages: WikiPage[];
}

interface TreeData {
  folders: Folder[];
  pages: WikiPage[];
}

interface WikiTreeResponse {
  success: boolean;
  data?: TreeData;
}

interface WikiPageResponse {
  success: boolean;
  data?: WikiPage;
}

interface WikiSearchResponse {
  success: boolean;
  data?: WikiPage[];
}

interface TreeNodeProps {
  node: TreeNode;
  onSelectPage: (page: WikiPage) => void;
}

const buildTree = (folders: Folder[], pages: WikiPage[]): TreeNode => {
  const folderMap = new Map<number | string | null, TreeNode>();
  const root: TreeNode = {
    id: null,
    name: "Root",
    parent_id: null,
    folders: [],
    pages: [],
  };
  folderMap.set(null, root);
  folders.forEach((f) => folderMap.set(f.id, { ...f, folders: [], pages: [] }));
  folders.forEach((f) => {
    const parent = folderMap.get(f.parent_id ?? null) || root;
    parent.folders.push(folderMap.get(f.id)!);
  });
  pages.forEach((p) => {
    const parent = folderMap.get(p.folder_id ?? null) || root;
    parent.pages.push(p);
  });
  return root;
};

const TreeNodeComponent: React.FC<TreeNodeProps> = ({ node, onSelectPage }) => {
  const [expanded, setExpanded] = useState(true);

  const handleFolderClick = () => setExpanded(!expanded);

  const handlePageClick = (page: WikiPage) => () => onSelectPage(page);

  return (
    <div className="tree-node">
      {node.id !== null && (
        <button
          type="button"
          className="tree-folder"
          onClick={handleFolderClick}
        >
          <span className="toggle">{expanded ? "▾" : "▸"}</span> 📁 {node.name}
        </button>
      )}
      {expanded && (
        <div className="tree-children">
          {node.folders.map((f) => (
            <TreeNodeComponent
              key={`f-${f.id}`}
              node={f}
              onSelectPage={onSelectPage}
            />
          ))}
          {node.pages.map((p) => (
            <button
              type="button"
              key={`p-${p.id}`}
              className="tree-page"
              onClick={handlePageClick(p)}
            >
              📄 {p.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Wiki: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [loading, setLoading] = useState(false);
  const [treeData, setTreeData] = useState<TreeData>({
    folders: [],
    pages: [],
  });
  const [selectedPage, setSelectedPage] = useState<WikiPage | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<WikiPage[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  const tree = useMemo(
    () => buildTree(treeData.folders || [], treeData.pages || []),
    [treeData],
  );

  const loadTree = useCallback(async () => {
    setLoading(true);
    try {
      const res = (await api.wiki.getTree()) as unknown as WikiTreeResponse;
      if (res.success && res.data) setTreeData(res.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const handleSelectPage = async (pageMeta: WikiPage) => {
    const res = (await api.wiki.getPage(
      pageMeta.id,
    )) as unknown as WikiPageResponse;
    if (res.success && res.data) {
      const pageData = res.data;
      setSelectedPage(pageData);
      setTitle(pageData.title || "");
      setContent(pageData.content || "");
      setIsEditMode(false);
    }
  };

  const handleNewFolder = async () => {
    const name = window.prompt("Folder name?");
    if (!name) return;
    await api.wiki.createFolder(name, null);
    await loadTree();
  };

  const handleNewPage = async () => {
    const titleInput = window.prompt("Page title?");
    if (!titleInput) return;
    const res = (await api.wiki.createPage(
      titleInput,
      null,
      "# New Page\n",
    )) as unknown as WikiPageResponse;
    if (res.success && res.data) {
      await loadTree();
      const pageData = res.data;
      await handleSelectPage({ id: pageData.id, title: titleInput });
      setIsEditMode(true);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setLoading(true);
      await api.wiki.downloadAll();
      // Download is handled automatically by the API function
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download wiki files. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    const res = (await api.wiki.updatePage(selectedPage.id, {
      title,
      content,
    })) as unknown as WikiPageResponse;
    if (res.success && res.data) {
      setSelectedPage(res.data);
      await loadTree();
      alert("Saved");
    }
  };

  const handleDeletePage = async () => {
    if (!selectedPage) return;
    if (!window.confirm("Delete this page?")) return;
    await api.wiki.deletePage(selectedPage.id);
    setSelectedPage(null);
    setTitle("");
    setContent("");
    await loadTree();
  };

  // Handle debounced search - triggered by SearchInput component
  const handleDebouncedSearch = async (searchText: string) => {
    const trimmed = searchText.trim();
    if (!trimmed) {
      setSearchResults([]);
      return;
    }
    try {
      const res = (await api.wiki.search(
        trimmed,
      )) as unknown as WikiSearchResponse;
      if (res.success && res.data) setSearchResults(res.data);
    } catch (error) {
      // Log error but don't interrupt user for live search
      console.debug("Live search error:", (error as Error).message);
    }
  };

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleContentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
  };

  return (
    <div className="wiki-layout">
      <aside className="wiki-sidebar">
        <div className="sidebar-header">
          <h3>Wiki</h3>
          <div className="sidebar-actions">
            <button className="btn btn-sm" onClick={handleNewFolder}>
              + Folder
            </button>
            <button className="btn btn-sm" onClick={handleNewPage}>
              + Page
            </button>
            <button
              className="btn-download"
              onClick={handleDownloadAll}
              disabled={loading}
              title="Download all wiki pages as ZIP"
            >
              {loading ? "⏳ Downloading..." : "📥 Download All"}
            </button>
          </div>
        </div>
        <div className="sidebar-search">
          <SearchInput
            value={search}
            onChange={setSearch}
            onDebouncedChange={handleDebouncedSearch}
            placeholder="Search pages..."
            ariaLabel="Search wiki pages"
            showClearButton
          />
        </div>
        {searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((r) => (
              <button
                type="button"
                key={r.id}
                className="search-item"
                onClick={() => handleSelectPage(r)}
              >
                🔎 {r.title}
              </button>
            ))}
          </div>
        )}
        <div className="tree">
          {loading ? (
            <div>Loading...</div>
          ) : (
            <TreeNodeComponent node={tree} onSelectPage={handleSelectPage} />
          )}
        </div>
      </aside>
      <section className="wiki-editor">
        {selectedPage ? (
          <>
            <div className="editor-header">
              {isEditMode ? (
                <input
                  className="title-input"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Page title"
                />
              ) : (
                <h2 className="title-display">{title || "Untitled"}</h2>
              )}
              <div className="editor-actions">
                {isEditMode ? (
                  <>
                    <button className="btn" onClick={handleSave}>
                      Save
                    </button>
                    <button
                      className="btn"
                      onClick={() => setIsEditMode(false)}
                    >
                      View
                    </button>
                    <button
                      className="btn btn-delete"
                      onClick={handleDeletePage}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <button className="btn" onClick={() => setIsEditMode(true)}>
                    Edit
                  </button>
                )}
              </div>
            </div>
            {isEditMode ? (
              <div className="editor-body">
                <textarea
                  className="md-input"
                  value={content}
                  onChange={handleContentChange}
                  placeholder="# Markdown content

## Example Table
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Data 1   | Data 2   | Data 3   |
| Data 4   | Data 5   | Data 6   |

## Example Code
```javascript
console.log('Hello World!');
```

## Example List
- Item 1
- Item 2
- Item 3"
                />
                <div className="md-preview">
                  <div
                    className="rendered"
                    dangerouslySetInnerHTML={{
                      __html: content
                        ? (marked(content) as string)
                        : '<p class="text-muted">Start typing to see preview...</p>',
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="editor-body preview-only">
                <div className="md-preview">
                  <div
                    className="rendered"
                    dangerouslySetInnerHTML={{
                      __html: content
                        ? (marked(content) as string)
                        : '<p class="text-muted">No content to display.</p>',
                    }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">Select or create a page to begin.</div>
        )}
      </section>
    </div>
  );
};

// Configure marked for security and proper rendering
marked.setOptions({
  breaks: true,
  gfm: true, // GitHub Flavored Markdown
});

// Custom renderer for better link handling
const renderer = new marked.Renderer();
renderer.link = function ({ href, title, tokens }) {
  const text = tokens
    ? tokens.map((t) => ("text" in t ? t.text : "")).join("")
    : "";
  const hrefStr = href || "";
  const isExternal =
    hrefStr.startsWith("http://") || hrefStr.startsWith("https://");
  const target = isExternal ? ' target="_blank" rel="noopener noreferrer"' : "";
  const titleAttr = title ? ` title="${title}"` : "";
  return `<a href="${hrefStr}"${titleAttr}${target}>${text}</a>`;
};

marked.use({ renderer });

export default Wiki;
